/**
 * File: UserProfileContext.tsx
 * Description: Provides the current user's profile to the app, so components
 *   like the top bar can react to user-level settings (e.g. whether the
 *   Azure DevOps integration is enabled).
 * Author: Archie Team
 * Created: 2026-05-22
 * Last Modified: 2026-05-22
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  UserProfileResponse,
  UserProfileService,
} from '../services/userProfileService';

import { useAuth } from './AuthContext';

interface UserProfileContextValue {
  profile: UserProfileResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  setProfile: (profile: UserProfileResponse) => void;
}

const UserProfileContext = createContext<UserProfileContextValue | undefined>(
  undefined,
);

interface UserProfileProviderProps {
  children: React.ReactNode;
}

/**
 * Provider that fetches the current user's profile once they are
 * authenticated and exposes it to descendants.
 */
export const UserProfileProvider: React.FC<UserProfileProviderProps> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [profile, setProfileState] = useState<UserProfileResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await UserProfileService.getMyProfile();
      setProfileState(data);
    } catch (error) {
      setError(
        error instanceof Error ? error.message : 'Failed to load user profile',
      );
      setProfileState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProfile();
    } else {
      setProfileState(null);
    }
  }, [isAuthenticated, fetchProfile]);

  const setProfile = useCallback((next: UserProfileResponse): void => {
    setProfileState(next);
  }, []);

  const value = useMemo<UserProfileContextValue>(
    () => ({
      profile,
      loading,
      error,
      refresh: fetchProfile,
      setProfile,
    }),
    [profile, loading, error, fetchProfile, setProfile],
  );

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};

/**
 * Hook to access the current user's profile.
 * Returns null values when used outside the provider (e.g. on public pages).
 */
export const useUserProfile = (): UserProfileContextValue => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    return {
      profile: null,
      loading: false,
      error: null,
      refresh: async () => {},
      setProfile: () => {},
    };
  }
  return context;
};
